import type { ScenePlan, ScenePlanSignature } from "./scene-plan.js";

export const RENDERER_KINDS = Object.freeze(["three", "canvas", "dom"] as const);
export const FRAME_POLICIES = Object.freeze(["none", "host-raf", "shared-coordinator"] as const);

export type SceneRendererKind = (typeof RENDERER_KINDS)[number];
export type RendererFramePolicy = (typeof FRAME_POLICIES)[number];
export type RendererFailurePhase = "initialize" | "apply" | "frame" | "destroy";
export type RendererFailureCode =
  | "initialization"
  | "unavailable"
  | "context-lost"
  | "apply"
  | "frame"
  | "resource"
  | "cleanup"
  | "unknown";

export interface RendererFailureCause {
  readonly name?: string;
  readonly message?: string;
  readonly code?: string | number;
}

export interface RendererFailure {
  readonly kind: SceneRendererKind;
  readonly phase: RendererFailurePhase;
  readonly code: RendererFailureCode;
  readonly message: string;
  readonly recoverable: boolean;
  readonly cause?: RendererFailureCause;
}

export type RendererFailureInput = Omit<RendererFailure, "cause"> & {
  readonly cause?: unknown;
};

/** A frozen visual snapshot reconstructed only from successfully applied renderer state. */
export type CanvasPathOperation =
  | Readonly<{ operation: "moveTo" | "lineTo"; x: number; y: number }>
  | Readonly<{
    operation: "arc";
    x: number;
    y: number;
    radius: number;
    startAngle: number;
    endAngle: number;
  }>;

export type CanvasDrawCommand =
  | Readonly<{
    operation: "clearRect";
    rect: readonly [number, number, number, number];
    transform: readonly [number, number, number, number, number, number];
  }>
  | Readonly<{
    operation: "fillRect";
    rect: readonly [number, number, number, number];
    transform: readonly [number, number, number, number, number, number];
    fillStyle: string;
    alpha: number;
  }>
  | Readonly<{
    operation: "stroke";
    path: readonly CanvasPathOperation[];
    transform: readonly [number, number, number, number, number, number];
    strokeStyle: string;
    lineWidth: number;
    alpha: number;
  }>;

export interface CanvasCommandTraceSnapshot {
  readonly kind: "canvas-command-trace";
  readonly commands: readonly CanvasDrawCommand[];
}

interface ResourceVisualSnapshot {
  readonly bounds: Readonly<{ width: number; height: number; pixelRatio: number }>;
  readonly paper: Readonly<{
    color: string;
    grainColor: string;
    grainOpacity: number;
    grainScalePx: number;
  }>;
  readonly wash: Readonly<{
    color: string;
    opacity: number;
    coverage: number;
    rect: Readonly<{ x: number; y: number; width: number; height: number }>;
  }>;
  readonly rain: readonly Readonly<{
    startX: number;
    startY: number;
    endX: number;
    endY: number;
    color: string;
    alpha: number;
  }>[];
  readonly ripples: readonly Readonly<{
    centerX: number;
    centerY: number;
    radius: number;
    color: string;
    alpha: number;
  }>[];
}

export interface ThreeResourceSnapshot extends ResourceVisualSnapshot {
  readonly kind: "three-resources";
}

export interface DomStyleSnapshot extends ResourceVisualSnapshot {
  readonly kind: "dom-styles";
}

export type RendererVisualSnapshot =
  | CanvasCommandTraceSnapshot
  | ThreeResourceSnapshot
  | DomStyleSnapshot;

export interface RendererObservation {
  readonly kind: SceneRendererKind;
  readonly applyCount: number;
  readonly frameCount: number;
  readonly renderCount: number;
  readonly observationBuildCount?: number;
  readonly renderState: RendererVisualSnapshot | null;
  readonly viewport: Readonly<{ width: number; height: number; pixelRatio: number }>;
  readonly resources: Readonly<{
    canvasCount: number;
    objectCount: number;
    activeSubscriptions: number;
    releaseAttempts: number;
    releaseFailures: number;
  }>;
}

export interface RendererDiagnostics {
  readonly kind: "three" | "canvas" | "dom";
  readonly signature: ScenePlanSignature | null;
  readonly framePolicy: "none" | "host-raf" | "shared-coordinator";
  /** Present when an adapter can verify every owned teardown obligation. */
  readonly cleanupComplete?: boolean;
  readonly canvasCount: number;
  readonly objectCount: number;
  readonly activeSubscriptions: number;
  readonly allocationsAfterApply: number;
  readonly lastFailure: RendererFailure | null;
  readonly observation: RendererObservation;
}

export interface SceneRenderer {
  readonly kind: "three" | "canvas" | "dom";
  apply(nextScenePlan: ScenePlan): void;
  frame(nowMs: number): void;
  onFailure(listener: (failure: RendererFailure) => void): () => void;
  diagnostics(): RendererDiagnostics;
  destroy(): void;
}

/**
 * Construction is owned by adapter factories; initialization failures are
 * reported from factory construction rather than an initialize lifecycle
 * method. Viewport/DPR observation and coordinate mapping are resources owned
 * by each adapter outside ScenePlan. They map declared renderer-neutral units
 * only and never introduce raw visual or ambience authority.
 */
export const RENDERER_CONTRACT_INVARIANTS = Object.freeze({
  hostOwns: Object.freeze(["selection", "sole-frame-subscription", "adapter-replacement"] as const),
  adapterOwns: Object.freeze([
    "created-resources",
    "created-resource-cleanup",
    "viewport-mapping",
  ] as const),
  initializeFailuresOccurDuringFactoryConstruction: true,
  viewportMappingHasRawVisualAuthority: false,
  applyStartsFrameLoop: false,
  acceptsRawAmbience: false,
  ambienceChangesRequireResolution: true,
  destroyIsIdempotent: true,
  failureListenersReturnUnsubscribe: true,
});

function safeCauseField(cause: object, key: "name" | "message" | "code"): unknown {
  try {
    return Reflect.get(cause, key);
  } catch {
    return undefined;
  }
}

function normalizeFailureCause(cause: unknown): RendererFailureCause | undefined {
  if (cause === undefined || cause === null) return undefined;
  if (typeof cause === "string") return Object.freeze({ message: cause });
  if (typeof cause !== "object" && typeof cause !== "function") return undefined;

  const metadata: { name?: string; message?: string; code?: string | number } = {};
  const name = safeCauseField(cause, "name");
  const message = safeCauseField(cause, "message");
  const code = safeCauseField(cause, "code");
  if (typeof name === "string") metadata.name = name;
  if (typeof message === "string") metadata.message = message;
  if (typeof code === "string" || (typeof code === "number" && Number.isFinite(code))) {
    metadata.code = code;
  }
  return Object.keys(metadata).length === 0 ? undefined : Object.freeze(metadata);
}

export function createRendererFailure(failure: RendererFailureInput): RendererFailure {
  const cause = normalizeFailureCause(failure.cause);
  const snapshot: RendererFailure = cause === undefined
    ? {
      kind: failure.kind,
      phase: failure.phase,
      code: failure.code,
      message: failure.message,
      recoverable: failure.recoverable,
    }
    : {
      kind: failure.kind,
      phase: failure.phase,
      code: failure.code,
      message: failure.message,
      recoverable: failure.recoverable,
      cause,
    };
  return Object.freeze(snapshot);
}

export interface RendererFailureChannel {
  onFailure(listener: (failure: RendererFailure) => void): () => void;
  emit(failure: RendererFailureInput): void;
  lastFailure(): RendererFailure | null;
  activeSubscriptions(): number;
  destroy(): void;
}

export function createRendererFailureChannel(): RendererFailureChannel {
  const listeners = new Set<(failure: RendererFailure) => void>();
  let latest: RendererFailure | null = null;
  let destroyed = false;

  return Object.freeze({
    onFailure(listener: (failure: RendererFailure) => void): () => void {
      if (destroyed) return (): void => undefined;
      listeners.add(listener);
      let subscribed = true;
      return (): void => {
        if (!subscribed) return;
        subscribed = false;
        listeners.delete(listener);
      };
    },
    emit(failure: RendererFailureInput): void {
      if (destroyed) return;
      const snapshot = createRendererFailure(failure);
      latest = snapshot;
      for (const listener of [...listeners]) {
        try {
          listener(snapshot);
        } catch {
          // Listener isolation is deliberate: host fallback listeners must all run.
        }
      }
    },
    lastFailure(): RendererFailure | null {
      return latest;
    },
    activeSubscriptions(): number {
      return listeners.size;
    },
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      listeners.clear();
    },
  });
}
